import { useAnimatedList, Spinner } from '../components/feedback';
import { addonKindsOf, addonLoaderOf } from '@mcpanel/core';
import { OperationBoundary } from '../components/OperationBoundary';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { ServerModpack } from '../components/ServerModpack';
import { ManualAddons } from '../components/ManualAddons';
import { ServerClientPackProblems } from '../components/ServerClientPackProblems';
import { launcherApi } from '../api/launcher';
import { LauncherPolicyCard } from './LauncherPolicyCard';
import { useState } from 'react';
import { api } from '../api/client';
import { api3 } from '../api/client-p3';
import type { ChangeRequest, RequestKind, ServerView } from '../api/types';
import type { ModEntry, ModLoader, ModResolveResult, ModSpecifier, ServerMods } from '../api/types-p3';
import { errorMessage, useApp } from '../app-context';
import { AddonIcon, AddonSearch, sideLabel, SourceTag } from '../components/AddonSearch';
import { RequestReview } from '../components/RequestReview';
import { Badge, Dialog, Empty, ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';

type StagedAdd = ModSpecifier & { title: string };
/** クライアント専用（サーバーには置かず、配布物にだけ入る）Mod */
const clientOnly = (entry: ModEntry) => entry.kind === 'mod' && entry.env.server === 'unsupported';
const keyOf = (entry: { source?: string; projectId: string }) => `${entry.source ?? 'modrinth'}:${entry.projectId}`;

function PreviewDialog({
  preview,
  word,
  busy,
  onBack,
  onSubmit,
}: {
  preview: ModResolveResult;
  word: string;
  busy: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const list = (items: ModEntry[], dependencyNote: boolean) => (
    <ul className="small mod-list" style={{ margin: '4px 0 0', paddingLeft: 20 }}>
      {items.map((m) => (
        <li key={keyOf(m)}>
          {m.title} {m.versionNumber} <SourceTag source={m.source} />
          {dependencyNote && m.dependency && <span className="tag">依存で自動追加</span>}
          {clientOnly(m) && <span className="tag">配布物だけ</span>}
        </li>
      ))}
    </ul>
  );
  return (
    <Dialog
      title={`${word}の変更内容`}
      wide
      onClose={onBack}
      actions={
        <>
          <button className="btn" onClick={onBack}>
            戻る
          </button>
          <button className="btn primary" disabled={busy || preview.errors.length > 0} onClick={onSubmit}>
            {busy && <Spinner />}
            この内容で変更を依頼
          </button>
        </>
      }
    >
      {preview.errors.map((e) => (
        <div key={e.project} className="alert danger">
          {e.project}: {e.message}
        </div>
      ))}
      {preview.added.length > 0 && (
        <div>
          <h3>追加</h3>
          {list(preview.added, true)}
        </div>
      )}
      {preview.removed.length > 0 && (
        <div>
          <h3>削除</h3>
          {list(preview.removed, false)}
        </div>
      )}
      <p className="small">
        変更後の{word}数: {preview.entries.length}
        {[...preview.added, ...preview.removed, ...preview.updated.map((u) => u.after)].length > 0 &&
        [...preview.added, ...preview.removed, ...preview.updated.map((u) => u.after)].every(clientOnly)
          ? '。クライアント専用の Mod だけの変更なので、サーバーは止めずに配布物だけを変えます（起動テストに受かってから配ります）。'
          : '。適用時はサーバーを停止し、スナップショットを取ってから入れ替えます。'}
      </p>
    </Dialog>
  );
}

function ModsTabContent({ server, kind }: { server: ServerView; kind: 'mod' | 'plugin' }) {
  const { canOperate, notify } = useApp();
  const mods = usePoll(() => api3.serverMods(server.id), 30000, [server.id]);
  const [add, setAdd] = useState<StagedAdd[]>([]);
  const [remove, setRemove] = useState<string[]>([]);
  const [preview, setPreview] = useState<ModResolveResult>();
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const stagedList = useAnimatedList(add, (mod) => `${mod.source}:${mod.project}`);
  useEditDirty(add.length > 0 || remove.length > 0);
  const family = server.software.family;
  const plugins = kind === 'plugin';
  const word = plugins ? 'プラグイン' : 'Mod';

  if (!addonKindsOf(family).mods && !addonKindsOf(family).plugins) {
    return (
      <div className="stack">
        <Empty>
          このサーバーは {family} です。Mod を入れるには Fabric など、プラグインを入れるには Paper
          に「ソフト・バージョン」タブで変更してください。
        </Empty>
      </div>
    );
  }

  const entries = (mods.data?.entries ?? []).filter((m) => m.kind === kind);
  const installed = new Set(entries.map(keyOf));
  const pending = add.length > 0 || remove.length > 0;
  const change = () => ({
    kind,
    add: add.map(({ project, version, source }) => ({ project, version, source })),
    remove,
    update: [] as string[],
  });
  const reset = () => {
    setAdd([]);
    setRemove([]);
  };

  const resolve = async () => {
    setBusy(true);
    try {
      setPreview(
        await api3.resolveMods({
          loader: addonLoaderOf(family, kind) as ModLoader,
          mcVersion: server.software.mcVersion,
          serverId: server.id,
          ...change(),
        }),
      );
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const created = await api.createRequest('change_mods' as RequestKind, change(), server.id);
      setPreview(undefined);
      setRequest(created);
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const setSide = async (entry: ModEntry, value: string) => {
    try {
      await launcherApi.side(server.id, entry, value === 'auto' ? null : (value as ModEntry['clientOverride']));
      await mods.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const row = (entry: ModEntry) => {
    const removing = remove.includes(entry.projectId);
    return (
      <tr key={keyOf(entry)} className={removing ? 'unchanged' : undefined}>
        <td>
          <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
            <AddonIcon url={entry.iconUrl} title={entry.title} />
            <div>
              <div>
                {entry.title} <SourceTag source={entry.source} />
                {entry.modpack ? (
                  <span className="tag">Modpack: {entry.modpack.name}</span>
                ) : (
                  <span className="tag">個別に追加</span>
                )}
                {entry.dependency && <span className="tag">依存</span>}
                {clientOnly(entry) && (
                  <span className="tag" title="サーバーには置かず、ランチャーの配布物にだけ入れます">
                    配布物だけ
                  </span>
                )}
              </div>
              <div className="small faint">{entry.versionNumber}</div>
            </div>
          </div>
        </td>
        <td className="small muted">
          {plugins ? (
            ''
          ) : (
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <select
                className="input"
                style={{ width: 300 }}
                aria-label={entry.title + ' のクライアント区分'}
                title="プレイヤーの PC に入れるか"
                disabled={!canOperate}
                value={entry.clientOverride ?? 'auto'}
                onChange={(e) => void setSide(entry, e.target.value)}
              >
                <option value="auto">配布元に従う: {sideLabel(entry)}</option>
                <option value="required">必須</option>
                <option value="optional">任意</option>
                {!clientOnly(entry) && <option value="unsupported">不要（サーバーのみ）</option>}
              </select>
              {entry.clientOverride && entry.clientOverride !== entry.env.client && <span className="tag">手動</span>}
            </div>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>
          {canOperate && !entry.dependency && (
            <button
              className="btn ghost small danger"
              onClick={() =>
                setRemove(removing ? remove.filter((p) => p !== entry.projectId) : [...remove, entry.projectId])
              }
            >
              {removing ? '削除を取り消す' : '削除'}
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="stack">
      {!plugins && <ServerModpack serverId={server.id} />}
      {!plugins && (
        <div data-search-id="mod-verify">
          <ServerClientPackProblems
            serverId={server.id}
            modpackProjects={entries.filter((entry) => entry.modpack).map((entry) => entry.projectId)}
            removable={entries.filter((entry) => !entry.dependency).map((entry) => entry.projectId)}
            remove={(id) => setRemove((current) => (current.includes(id) ? current : [...current, id]))}
          />
        </div>
      )}
      <ErrorAlert error={mods.error} />
      {stagedList.items.length > 0 && (
        <div className="stack mod-list">
          {stagedList.items.map((mod) => (
            <div
              key={mod.source + ':' + mod.project}
              ref={stagedList.ref}
              className={stagedList.className(mod)}
              inert={!add.some((entry) => entry === mod)}
            >
              <span>{mod.title}</span>{' '}
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setAdd((current) => current.filter((entry) => entry !== mod))}
              >
                追加予定から外す
              </button>
            </div>
          ))}
        </div>
      )}
      {pending && (
        <div className="card card-body row" style={{ borderColor: 'var(--accent)' }}>
          <span>
            変更予定: 追加 {add.length} 件{add.length > 0 && `（${add.map((a) => a.title).join('、')}）`}・ 削除{' '}
            {remove.length} 件
          </span>
          <span className="spacer" />
          <button className="btn primary small" disabled={busy} onClick={() => void resolve()}>
            {busy && <Spinner />}
            依存関係を確認
          </button>
        </div>
      )}
      <section className="card" data-search-id={plugins ? 'plugins' : 'mods'}>
        <div className="card-header">
          <h2>
            導入済みの{word}（{entries.length}）
          </h2>
          {plugins && <span className="small faint">plugins フォルダに配置します</span>}
        </div>
        {entries.length === 0 ? (
          <Empty>{word}はまだありません。</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              {!plugins && (
                <thead>
                  <tr>
                    <th>Mod</th>
                    <th data-search-id="mod-side">プレイヤーの PC に</th>
                    <th />
                  </tr>
                </thead>
              )}
              <tbody>{entries.map(row)}</tbody>
            </table>
          </div>
        )}
        {plugins && (
          <div className="card-body small muted">
            ここで入れたもの以外の jar（下の「手動で入れたプラグイン」）には触れません。
          </div>
        )}
      </section>

      <ManualAddons serverId={server.id} kind={kind} />

      {canOperate && (
        <AddonSearch
          family={family}
          kind={kind}
          mcVersion={server.software.mcVersion}
          sources={mods.data?.sources}
          stateOf={(hit) =>
            installed.has(keyOf(hit))
              ? 'installed'
              : add.some((s) => s.project === hit.projectId && s.source === hit.source)
                ? 'staged'
                : undefined
          }
          onAdd={(hit) => setAdd([...add, { project: hit.projectId, title: hit.title, source: hit.source }])}
        />
      )}

      {preview && (
        <PreviewDialog
          preview={preview}
          word={word}
          busy={busy}
          onBack={() => setPreview(undefined)}
          onSubmit={() => void submit()}
        />
      )}

      {request && (
        <Dialog
          title="変更内容の確認"
          wide
          onClose={() => {
            setRequest(undefined);
            reset();
          }}
          actions={null}
        >
          <RequestReview
            request={request}
            compact
            onDecided={() => {
              setRequest(undefined);
              reset();
              void mods.reload();
            }}
          />
        </Dialog>
      )}
      {!canOperate && <Badge tone="muted">閲覧のみ</Badge>}
    </div>
  );
}

export function ModsTab({ server }: { server: ServerView }) {
  return (
    <OperationBoundary serverId={server.id}>
      <EditBoundary resource={{ target: `server:${server.id}`, screen: 'mods' }} revision={String(server.revision)}>
        {addonKindsOf(server.software.family).mods && <ModsTabContent server={server} kind="mod" />}
        {addonKindsOf(server.software.family).plugins && <ModsTabContent server={server} kind="plugin" />}
      </EditBoundary>
    </OperationBoundary>
  );
}
